import "./dashboard.css";
import { useLocation, useNavigate } from "react-router-dom";
import RatingBar from "../components/ratingstars";
import { useState, useMemo } from "react";
import Checkbox from "../components/checkbox";
import toast, { Toaster } from "react-hot-toast";
import { useAuth } from "@clerk/clerk-react";
import Confetti from "react-confetti";

export default function DashboardV2() {
  
  const { getToken } = useAuth();
  const location = useLocation();
  const { flow_id, skillGapData, roadmapData: initialRoadmapData, flow_name, source } = location.state || {};
  const [showConfetti, setShowConfetti] = useState(false);

  const [roadmapData, setRoadmapData] = useState(initialRoadmapData);
  const [activeRoadmap, setActiveRoadmap] = useState(null);
  const [activeSkill, setActiveSkill] = useState(null);
  const [isSaved, setSaved] = useState(false);
  const [index, setIndex] = useState(null);
  const [isUpdated, setUpdated] = useState(false);
  const [viewMode, setViewMode] = useState("roadmap");
  const [selectedDepth, setSelectedDepth] = useState("all");
  
  const navigate = useNavigate();

  const parseScore = (score) => {
    if (!score) return { current: 0, max: 5 };
    
    if (typeof score === 'string' && score.includes('/')) {
      const [current, max] = score.split('/').map(s => parseInt(s.trim()));
      return { current: current || 0, max: max || 5 };
    }
    
    if (typeof score === 'number') {
      return { current: score, max: 5 };
    }
    
    return { current: 0, max: 5 };
  };

  const getRatingValue = (score) => {
    const parsed = parseScore(score);
    return Math.round((parsed.current / parsed.max) * 100);
  };

  const overallProgress = useMemo(() => {
    if (!roadmapData?.roadmap) return 0;
    const total = roadmapData.roadmap.reduce((sum, item) => sum + (item.fix?.length || 0), 0);
    const completed = roadmapData.roadmap.reduce((sum, item) => {
      if (!item.completion_status) return sum;
      return sum + Object.values(item.completion_status).filter(Boolean).length;
    }, 0);
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  }, [roadmapData]);

  const hireabilityBoost = useMemo(() => {
    if (!roadmapData?.roadmap) return 0;
    return roadmapData.roadmap.reduce((sum, item) => {
      if (!item.completion_status) return sum;
      const allDone = Object.values(item.completion_status).every(status => status === true);
      return sum + (allDone ? (item.hireability_impact || 5) : 0);
    }, 0);
  }, [roadmapData]);

  const suggestedRoadmaps = useMemo(() => {
    if (!roadmapData?.suggested_starting_roadmaps || !roadmapData?.roadmap) return [];
    return roadmapData.suggested_starting_roadmaps
      .slice(0, 3)
      .map(idx => roadmapData.roadmap[idx])
      .filter(Boolean);
  }, [roadmapData]);

  const visibleRoadmaps = useMemo(() => {
    if (!roadmapData?.roadmap) return [];
    if (selectedDepth === "all") return roadmapData.roadmap;
    const count = parseInt(selectedDepth);
    return roadmapData.roadmap.slice(0, count);
  }, [roadmapData, selectedDepth]);

  const allResources = useMemo(() => {
    if (!roadmapData?.roadmap) return [];
    const resources = [];
    roadmapData.roadmap.forEach(item => {
      if (item.resources && item.resources.length > 0) {
        item.resources.forEach(resource => {
          resources.push({
            ...resource,
            skill: item.skill
          });
        });
      }
    });
    return resources;
  }, [roadmapData]);

  const allUnlockedRoles = useMemo(() => {
    if (!roadmapData?.roadmap) return [];
    const rolesMap = new Map();
    roadmapData.roadmap.forEach((item, idx) => {
      if (item.unlocks_roles && item.unlocks_roles.length > 0) {
        item.unlocks_roles.forEach(role => {
          if (!rolesMap.has(role)) {
            rolesMap.set(role, []);
          }
          rolesMap.get(role).push({ skill: item.skill, index: idx });
        });
      }
    });
    return Array.from(rolesMap.entries()).map(([role, skills]) => ({ role, skills }));
  }, [roadmapData]);

  const handleRoadmapOpen = (item, i) => {
    setIndex(i);
    setActiveRoadmap(item);
    setShowConfetti(false);
  };

  const handleRoadmapClose = () => setActiveRoadmap(null);
  const handleSkillOpen = (skill, details) => setActiveSkill({ skill, details });
  const handleSkillClose = () => setActiveSkill(null);

  const handleSave = async () => {
    const t = toast.loading("Saving your flow...", { position: "bottom-right" });
    try {
      const token = await getToken();
      const res = await fetch("https://jobsniper-be-production.up.railway.app/saveFlow", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          skill_gap: skillGapData,
          roadmap: roadmapData,
          flow_name
        })
      });

      if (res.ok) {
        setSaved(true);
        toast.success("Saved successfully!", { position: "bottom-right", id: t });
      } else {
        toast.error("Unable to save.", { position: "bottom-right", id: t });
      }
    } catch (error) {
      toast.error("Network error.", { position: "bottom-right", id: t });
    }
  };

  const handlePatchSave = async () => {
    const t = toast.loading("Updating...", { position: "bottom-right" });
    try {
      const token = await getToken();
      const res = await fetch("https://jobsniper-be-production.up.railway.app/updateFlow", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          skill_gap: skillGapData,
          roadmap: roadmapData,
          flow_name,
          flow_id: flow_id
        }),
      });

      if (res.ok) {
        setUpdated(true);
        toast.success("Updated!", { position: "bottom-right", id: t });
        setTimeout(() => navigate("/profile"), 1000);
      } else {
        toast.error("Unable to update.", { position: "bottom-right", id: t });
      }
    } catch (error) {
      toast.error("Network error.", { position: "bottom-right", id: t });
    }
  };

  const handleReEvaluate = () => {
    const newOverallFit = Math.min(
      parseInt(skillGapData.overall_job_fit) + hireabilityBoost,
      100
    );
    toast.success(`Hireability: ${skillGapData.overall_job_fit}% → ${newOverallFit}%`, {
      position: "bottom-right",
      duration: 4000
    });
  };

  const toggleStep = (i) => {
    const stepKey = `step ${i + 1}`;

    setRoadmapData(prev => {
      const updatedRoadmap = [...prev.roadmap];
      const current = updatedRoadmap[index];

      if (!current.completion_status) {
        current.completion_status = {};
      }

      const updatedCompletion = {
        ...current.completion_status,
        [stepKey]: !current.completion_status[stepKey],
      };

      const updatedItem = {
        ...current,
        completion_status: updatedCompletion,
      };

      updatedRoadmap[index] = updatedItem;
      setActiveRoadmap(updatedItem);

      const allDone = (current.fix || []).every(
        (_, idx) => updatedCompletion[`step ${idx + 1}`] === true
      );

      if (allDone) {
        setShowConfetti(true);
        toast.success(`${current.skill} mastered! +${current.hireability_impact || 5}%`, {
          position: "bottom-right",
          duration: 3000
        });
      }

      return { ...prev, roadmap: updatedRoadmap };
    });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "beginner": return "#4ade80";
      case "intermediate": return "#fbbf24";
      case "advanced": return "#f87171";
      default: return "#94a3b8";
    }
  };

  const getCompletionStatus = (item) => {
    if (!item.completion_status || !item.fix) {
      return { completed: 0, total: item.fix?.length || 0, percentage: 0 };
    }
    const completed = Object.values(item.completion_status).filter(Boolean).length;
    const total = item.fix.length;
    return { completed, total, percentage: Math.round((completed / total) * 100) };
  };

  if (!skillGapData || !roadmapData) {
    return <div className="dashboard_v2">Unable to retrieve data</div>;
  }

  return (
    <div className="dashboard_v2">
      <Toaster />

      <header className="dashboard_header">
        <div className="header_content">
          <h1 className="header_content_h1">{flow_name}</h1>
          <div className="header_stats">
            <div className="stat_pill">
              <span className="stat_label">Overall Fit</span>
              <span className="stat_value">{skillGapData.overall_job_fit}%</span>
            </div>
            <div className="stat_pill boost">
              <span className="stat_label">Hireability Boost</span>
              <span className="stat_value">+{hireabilityBoost}%</span>
            </div>
            <div className="stat_pill">
              <span className="stat_label">Progress</span>
              <span className="stat_value">{overallProgress}%</span>
            </div>
          </div>
        </div>

        <div className="view_toggle">
          <button
            className={viewMode === "skills" ? "active" : ""}
            onClick={() => setViewMode("skills")}
          >
            Skill Gaps
          </button>
          <button
            className={viewMode === "roadmap" ? "active" : ""}
            onClick={() => setViewMode("roadmap")}
          >
            Roadmap
          </button>
          <button
            className={viewMode === "insights" ? "active" : ""}
            onClick={() => setViewMode("insights")}
          >
            Insights
          </button>
          <button
            className={viewMode === "resources" ? "active" : ""}
            onClick={() => setViewMode("resources")}
          >
            Resources
          </button>
          <button
            className={viewMode === "opportunities" ? "active" : ""}
            onClick={() => setViewMode("opportunities")}
          >
            Opportunities
          </button>
        </div>
      </header>

      <main className="dashboard_main">
        
        {/* Skills View */}
        {viewMode === "skills" && (
          <div className="skills_view">
            <div className="view_header">
              <h2>Skill Gap Analysis</h2>
              <p>Click on any skill to see detailed analysis</p>
            </div>
            <div className="skill_grid">
              {Object.entries(skillGapData.skill_gap || {}).map(([skill, details]) => (
                <button
                  key={skill}
                  className="skill_card"
                  onClick={() => handleSkillOpen(skill, details)}
                >
                  <h3>{skill}</h3>
                  <div className="skill_score">
                    <RatingBar value={getRatingValue(details.score)} max={100} />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Roadmap View */}
        {viewMode === "roadmap" && (
          <div className="roadmap_view">
            <div className="roadmap_controls">
              <div className="depth_selector">
                <label>Roadmap Depth:</label>
                <div className="depth_buttons">
                  {["3", "5", "10", "all"].map(depth => (
                    <button
                      key={depth}
                      className={selectedDepth === depth ? "active" : ""}
                      onClick={() => setSelectedDepth(depth)}
                    >
                      {depth === "all" ? "All" : depth}
                      {depth !== "all" && roadmapData.total_effort_estimates && (
                        <span className="depth_meta">
                          {roadmapData.total_effort_estimates[`${depth}_roadmaps`]}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              <button className="re_evaluate_btn" onClick={handleReEvaluate}>
                ↻ Re-evaluate
              </button>
            </div>

            {suggestedRoadmaps.length > 0 && (
              <div className="suggested_section">
                <h3>Recommended Starting Points</h3>
                <p>Ranked by impact</p>
                <div className="suggested_roadmaps">
                  {suggestedRoadmaps.map((item, idx) => {
                    const actualIndex = roadmapData.roadmap.indexOf(item);
                    return (
                      <button
                        key={actualIndex}
                        className="suggested_card"
                        onClick={() => handleRoadmapOpen(item, actualIndex)}
                      >
                        <div className="suggested_rank">#{idx + 1}</div>
                        <div className="suggested_content">
                          <h4>{item.skill}</h4>
                          <div className="suggested_stats">
                            <span className="impact">+{item.hireability_impact || 5}%</span>
                            <span className="effort">{item.effort_estimate || "3-5h"}</span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="roadmap_grid_v2">
              {visibleRoadmaps.map((item, i) => {
                const status = getCompletionStatus(item);
                const isCompleted = status.percentage === 100;
                
                return (
                  <div
                    key={i}
                    className={`roadmap_card ${isCompleted ? "completed" : ""}`}
                    onClick={() => handleRoadmapOpen(item, i)}
                  >
                    <div className="card_header">
                      <h3>{item.skill}</h3>
                      {isCompleted && <span className="completion_badge">✓</span>}
                    </div>
                    <div className="card_metrics">
                      <div className="metric">
                        <span className="metric_label">Relevance</span>
                        <span className="metric_value">{item.relevance_score || 85}%</span>
                      </div>
                      <div className="metric">
                        <span className="metric_label">Effort</span>
                        <span className="metric_value">{item.effort_estimate || "3-5h"}</span>
                      </div>
                      <div className="metric impact_metric">
                        <span className="metric_label">Impact</span>
                        <span className="metric_value">+{item.hireability_impact || 5}%</span>
                      </div>
                    </div>
                    <div className="card_progress">
                      <div className="progress_bar">
                        <div className="progress_fill" style={{ width: `${status.percentage}%` }} />
                      </div>
                      <span className="progress_text">
                        {status.completed}/{status.total} steps
                      </span>
                    </div>
                    <div
                      className="difficulty_badge"
                      style={{ backgroundColor: getDifficultyColor(item.difficulty) }}
                    >
                      {item.difficulty || "intermediate"}
                    </div>
                  </div>
                );
              })}
            </div>

            {roadmapData.hireability_ceiling && selectedDepth !== "all" && (
              <div className="ceiling_info">
                <p>
                  Completing {selectedDepth} roadmaps → {" "}
                  <strong>{roadmapData.hireability_ceiling[`${selectedDepth}_roadmaps`]}%</strong>
                </p>
              </div>
            )}
          </div>
        )}

        {/* Insights View */}
        {viewMode === "insights" && (
          <div className="insights_view">
            {(roadmapData.initial_analysis || roadmapData["initial analysis"]) && (
              <div className="insight_card">
                <h3>Analysis</h3>
                <p>{roadmapData.initial_analysis || roadmapData["initial analysis"]}</p>
              </div>
            )}

            <div className="insight_card">
              <h3>Your Progress</h3>
              <div className="progress_summary">
                <div className="summary_stat">
                  <span className="stat_number">{overallProgress}%</span>
                  <span className="stat_desc">Completion</span>
                </div>
                <div className="summary_stat">
                  <span className="stat_number">
                    {roadmapData.roadmap.filter(item => {
                      if (!item.completion_status) return false;
                      return Object.values(item.completion_status).every(Boolean);
                    }).length}
                  </span>
                  <span className="stat_desc">Completed</span>
                </div>
                <div className="summary_stat">
                  <span className="stat_number">+{hireabilityBoost}%</span>
                  <span className="stat_desc">Gained</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Resources View - IMPROVED LAYOUT */}
        {viewMode === "resources" && (
          <div className="resources_view">
            <div className="view_header">
              <h2>Learning Resources</h2>
              <p>Curated resources for each skill</p>
            </div>

            {allResources.length > 0 ? (
              <div className="resources_list_view">
                {allResources.map((resource, idx) => (
                  <a
                    key={idx}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="resource_list_item"
                  >
                    <div className="resource_left">
                      <span className="resource_badge">{resource.type}</span>
                      <span className="resource_skill">{resource.skill}</span>
                    </div>
                    <div className="resource_center">
                      <h4>{resource.title}</h4>
                    </div>
                    <div className="resource_right">
                      <span className="resource_arrow">→</span>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <div className="empty_state">
                <p>No resources available yet.</p>
              </div>
            )}
          </div>
        )}

        {/* Opportunities View - ALTERNATIVE ROLES FIRST */}
        {viewMode === "opportunities" && (
          <div className="opportunities_view">
            <div className="view_header">
              <h2>Career Opportunities</h2>
              <p>Roles you can unlock</p>
            </div>

            {/* Alternative Career Paths FIRST */}
            {roadmapData.alternative_roles && roadmapData.alternative_roles.length > 0 && (
              <div className="insight_card">
                <h3>Alternative Career Paths</h3>
                <p>Pivot to these roles with training</p>
                <div className="alternative_roles_list">
                  {roadmapData.alternative_roles.map((role, idx) => (
                    <div key={idx} className="role_list_item">
                      <div className="role_header">
                        <h4>{role.role}</h4>
                        <div className="role_fit_inline">
                          <span>{role.current_fit}%</span>
                          <span className="arrow">→</span>
                          <span className="potential">{role.potential_fit}%</span>
                        </div>
                      </div>
                      <div className="role_requirements_inline">
                        <strong>Required:</strong>
                        {role.required_roadmaps?.map((roadmapIdx, skillIdx) => (
                          <button
                            key={skillIdx}
                            className="skill_tag_inline"
                            onClick={() => handleRoadmapOpen(roadmapData.roadmap[roadmapIdx], roadmapIdx)}
                          >
                            {roadmapData.roadmap[roadmapIdx]?.skill}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Unlocked Roles SECOND */}
            {allUnlockedRoles.length > 0 && (
              <div className="insight_card">
                <h3>Roles You Can Apply For</h3>
                <div className="unlocked_roles_list">
                  {allUnlockedRoles.map((item, idx) => (
                    <div key={idx} className="unlocked_role_item">
                      <h4>{item.role}</h4>
                      <div className="role_skills_inline">
                        <strong>Skills:</strong>
                        {item.skills.map((skill, sidx) => (
                          <button
                            key={sidx}
                            className="skill_tag_inline"
                            onClick={() => handleRoadmapOpen(roadmapData.roadmap[skill.index], skill.index)}
                          >
                            {skill.skill}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Compact Save Button */}
        <div className="save_section">
          {source !== "profile" ? (
            !isSaved && (
              <button className="save_btn" onClick={handleSave}>
                Save
              </button>
            )
          ) : (
            !isUpdated && (
              <button className="save_btn" onClick={handlePatchSave}>
                Back
              </button>
            )
          )}
        </div>
      </main>

      {/* Skill Overlay */}
      {activeSkill && (
        <div className="overlay_v2" onClick={handleSkillClose}>
          <div className="overlay_content_v2" onClick={(e) => e.stopPropagation()}>
            <div className="overlay_header">
              <h2>{activeSkill.skill}</h2>
              <button className="close_btn_v2" onClick={handleSkillClose}>×</button>
            </div>
            <div className="skill_score_section">
              <span className="score_label">Current Level</span>
              <div className="score_display">
                <RatingBar value={getRatingValue(activeSkill.details.score)} max={100} />
              </div>
            </div>
            <div className="analysis_section">
              <h3>Analysis</h3>
              <p>{activeSkill.details.analysis || "No analysis available."}</p>
            </div>
          </div>
        </div>
      )}

      {/* Roadmap Overlay */}
      {activeRoadmap && (
        <div className="overlay_v2" onClick={handleRoadmapClose}>
          {showConfetti && (
            <Confetti
              width={window.innerWidth}
              height={window.innerHeight}
              numberOfPieces={200}
              gravity={0.3}
              recycle={false}
            />
          )}
          <div className="overlay_content_v2 roadmap_overlay" onClick={(e) => e.stopPropagation()}>
            <div className="overlay_header">
              <div>
                <h2>{activeRoadmap.skill}</h2>
                {activeRoadmap.objective && <p className="objective">{activeRoadmap.objective}</p>}
              </div>
              <button className="close_btn_v2" onClick={handleRoadmapClose}>×</button>
            </div>

            {activeRoadmap.why_matters && (
              <div className="why_matters_section">
                <h3>Why This Matters</h3>
                <p>{activeRoadmap.why_matters}</p>
              </div>
            )}

            <div className="roadmap_meta">
              <div className="meta_item">
                <span className="meta_label">Difficulty</span>
                <span
                  className="meta_value difficulty_pill"
                  style={{ backgroundColor: getDifficultyColor(activeRoadmap.difficulty) }}
                >
                  {activeRoadmap.difficulty || "intermediate"}
                </span>
              </div>
              <div className="meta_item">
                <span className="meta_label">Effort</span>
                <span className="meta_value">{activeRoadmap.effort_estimate || "3-5h"}</span>
              </div>
              <div className="meta_item">
                <span className="meta_label">Impact</span>
                <span className="meta_value impact">+{activeRoadmap.hireability_impact || 5}%</span>
              </div>
            </div>

            <div className="steps_section">
              <h3>Learning Path</h3>
              <div className="steps_list">
                {(activeRoadmap.fix || []).map((step, i) => {
                  const isCompleted = activeRoadmap.completion_status?.[`step ${i + 1}`];
                  const isPreviousCompleted = i === 0 || activeRoadmap.completion_status?.[`step ${i}`];
                  const isLocked = !isPreviousCompleted && !isCompleted;

                  return (
                    <div key={i} className="step_wrapper">
                      <div className={`step_item ${isLocked ? "locked" : ""}`}>
                        <Checkbox
                          text={step}
                          completed={isCompleted || false}
                          onClick={() => {
                            if (!isLocked) toggleStep(i);
                          }}
                        />
                      </div>
                      {i !== (activeRoadmap.fix?.length ?? 0) - 1 && (
                        <div className={`step_connector ${isPreviousCompleted ? "active" : ""}`}>↓</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {activeRoadmap.resources && activeRoadmap.resources.length > 0 && (
              <div className="resources_section">
                <h3>Resources</h3>
                <div className="resources_list">
                  {activeRoadmap.resources.map((resource, idx) => (
                    <a
                      key={idx}
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`resource_link ${resource.type}`}
                    >
                      <span className="resource_type">{resource.type}</span>
                      <span className="resource_title">{resource.title}</span>
                      <span className="resource_arrow">→</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {activeRoadmap.unlocks_roles && activeRoadmap.unlocks_roles.length > 0 && (
              <div className="unlocks_section">
                <h3>Unlocks</h3>
                <div className="unlocked_roles">
                  {activeRoadmap.unlocks_roles.map((role, idx) => (
                    <span key={idx} className="role_pill">{role}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}